import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import { CartPageContext } from "@insite/content-library/Pages/CartPage";
import CartSaveOrderButton from "@insite/content-library/Widgets/Cart/CartSaveOrderButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@MasterBluePrint/Overrides/Components/CartTotalDisplay";
import { getE4FIP } from "@MasterBluePrint/Services/Eye4FraudService";
import checkoutCartTracking from "@MasterBluePrint/Tracking/CheckoutCart/checkoutCartTracking";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    const settingsCollection = getSettingsCollection(state);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;

    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        cartSettings: settingsCollection.cartSettings,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled: isCartLoading || !cart || isCartCheckoutDisabled(cart),
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading,
        isSubmitForQuoteButtonDisabled: isCartLoading,
        homeLink: getPageLinkByPageType(state, "HomePage"),
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    cancelPunchOutButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss; 
    sideContainerWrapper?: InjectableCss; 
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

export const cartTotalStyles: CartTotalStyles = {
    checkoutButton: {
        css: css`
            text-transform: uppercase;
            height: 44px;
            font-size: .875rem;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                            max-width: 310px;
                        `,
                        css`
                            width: 100%;
                            margin: .625rem 0 .9375rem 0;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                            max-width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cancelPunchOutButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            text-transform: uppercase;
            width: 100%;
            margin-top: .625rem;
            position: inherit;
            left: inherit;
            bottom: inherit;
            font-size: .875rem;
        `,
    },
    submitQuoteButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            text-transform: uppercase;
            width: 100%;
            margin-top: .625rem;
            position: inherit;
            left: inherit;
            bottom: inherit;
            border: none;
            font-size: .875rem;

            &:hover,
            &:active,
            &:focus {
                background: #fff !important;
                border: none !important;
                color: #002554 !important;
            }
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            text-transform: uppercase;
            width: 100%;
            margin-top: .625rem;
            position: inherit;
            left: inherit;
            bottom: inherit;
            font-size: .875rem;
        `,
    },
    submitRequisitionButton: {
        css: css`
            text-transform: uppercase;
            width: 100%;
            margin-top: .625rem;
            position: inherit;
            left: inherit;
            bottom: inherit;
            font-size: .875rem;
        `,
    },
    sideContainerWrapper: {
        css: css`
            background-color: #fff;
            box-shadow: 0 2px 4px 4px rgb(0 0 0 / 3%);
            border-radius: 2px;
            padding: 20px;

            &.hide {
                display: none;
            }

            button[data-test-selector="cart_saveOrder"] {
                color: #002554 !important;

                svg {
                    color: #002554 !important;
                }
                
                &:hover,
                &:active,
                &:focus {
                    color: #002554 !important;
                    border: none !important;
                    background: none !important;
                    padding: 0;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [   
                        null,
                        null,
                        null,
                        css`
                            padding: 1.5625rem 1.25rem 1.25rem;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    buttonWrapper: {
        css: css`
            background-color: #fff;
            padding: 10px 0 20px 0;
            &:empty {
                display: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [   
                    css`
                        text-align: center;
                    `,
                    null,
                    null,
                    css`
                        padding: .625rem 0 3.125rem 0;
                    `,
                ],
                "min",
            )}

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [   
                    null,
                    null,
                    css`
                        padding: 1.875rem 0 2.5rem 0;
                    `,
                    null,
                    null,
                ],
                "max",
            )}


        `,
    },
    centeringWrapper: {
        css: css`
            background: rgba(0, 37, 84, 0.2);
            height: 100%;
            width: 100%;
            position: fixed;
            z-index: 99;
            left: 0;
            top: 0;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
            color: #002554;
        `,
    }
};

const styles = cartTotalStyles;

const CartTotal: FC<Props> = ({
    cartSettings,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    preloadCheckoutShippingData,
    history,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    homeLink,
}) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [isCheckoutLoader,setCheckoutLoader] = useState(false);

    const checkoutHandler = () => {
        setCheckoutLoader(true); 
        if(cart != null){
            getE4FIP(cart.orderNumber);
            checkoutCartTracking(cart);
        }        
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { canBypassCheckoutAddress } = cart!;
        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            onSuccess: () => {
                history.push(checkoutShippingPageUrl!);
            },
        });       
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Submit for a Quote");

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    return (
        <Typography as="div" {...styles.sideContainerWrapper} className={`hide-top-view-desktop ${cart?.cartLines && cart?.cartLines?.length === 0 ? 'hide' : 'shown'}`}>
            <Hidden above="md">
                <CartSaveOrderButton variant="button" extendedStyles={styles.saveOrderButton} />
            </Hidden>
            {cart?.cartLines && cart?.cartLines?.length > 0 && (
                <CartTotalDisplay
                    showTaxAndShipping={cartSettings.showTaxAndShipping}
                    extendedStyles={styles.cartTotal}
                    isLoading={isLoading}
                    isCartEmpty={isCartEmpty}
                    cart={cart}
                    orderPromotions={orderPromotions}
                    shippingPromotions={shippingPromotions}
                    discountTotal={discountTotal}
                />
            )}

            <Typography as="div" {...styles.buttonWrapper}>
                {cart?.cartLines && cart?.cartLines?.length > 0 && (canCheckoutWithCart || cart.properties.isquote) && (
                    <>
                        {isCheckoutLoader && <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>}
                        
                        <Button
                            {...styles.checkoutButton}
                            onClick={checkoutHandler}
                            disabled={!cart.properties.isquote && (isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts) }
                            data-test-selector="cartTotal_Checkout"
                        >
                            {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Proceed to Checkout")}
                        </Button>
                        {isPunchOutOrder && (
                            <Button
                                {...styles.cancelPunchOutButton}
                                disabled={isPunchoutButtonDisabled}
                                onClick={cancelPunchOutHandler}
                            >
                                {translate("Cancel PunchOut")}
                            </Button>
                        )}
                        <TwoButtonModal
                            headlineText=""
                            modalIsOpen={quoteRequiredModalIsOpen}
                            messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                            cancelButtonText={translate("Cancel")}
                            submitButtonText={translate("Continue")}
                            onCancel={() => setQuoteRequiredModalIsOpen(false)}
                            onSubmit={checkoutHandler}
                            submitTestSelector="QuoteInCartMessageSubmit"
                        ></TwoButtonModal>
                    </>
                )}
                {cart?.cartLines && cart?.cartLines?.length > 0 && canSubmitForQuote && (
                    <Button
                        {...styles.submitQuoteButton}
                        disabled={isSubmitForQuoteButtonDisabled}
                        onClick={submitForQuoteClickHandler}
                        data-test-selector="cartTotal_SubmitQuote"
                    >
                        {submitForQuoteLabel}
                    </Button>
                )}
                {canSubmitRequisition && (
                    <Button
                        {...styles.submitRequisitionButton}
                        onClick={submitRequisitionClickHandler}
                        data-test-selector="cartTotal_SubmitRequisition"
                    >
                        {translate("Submit Requisition")}
                    </Button>
                )}
            </Typography>
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "ShoppingCart",
        allowedContexts: [CartPageContext],
    },
};

export default widgetModule;
